import type { LoaderArgs, PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import { useEffect } from "preact/hooks";
import { IcoCalendar, IcoFileText, IcoPlus } from "../components/icons";
import { AppLayout } from "../components/layout";
import {
  BrandedLayout,
  Button,
  GroupedHistoryList,
  PendingCheckInCard,
  PersonalizedGreeting,
  TeamCheckInsCard,
  type TeamMemberStatus,
  UserContextCard,
} from "../components/ui";
import { useHistory, usePageTitle } from "../hooks";
import { rpc } from "../rpc";
import type { MimicState } from "../stores/mimic";
import {
  type AssignedForm,
  type FormAssignment,
  HISTORY_PAGE_SIZE,
  type HistoryItem,
  isAdmin,
  type OrganizationListItem,
  type User,
} from "../types";
import { formatDateShort } from "../utils/format";
import {
  getMondayFromWeekKey,
  getWeekKeyFromDate,
} from "../utils/period-grouping";

type DirectReport = {
  id: string;
  name: string | null;
};

type HomeState = {
  currentUser: User;
  mimic: MimicState;
  organizations: OrganizationListItem[];
  initialOrgId: string | null;
  initialForms: AssignedForm[];
  formAssignments: FormAssignment[];
  initialHistory: HistoryItem[];
  initialHasMore: boolean;
  requestedOrgId: string | null;
  // Team data (for managers)
  teamMembers: TeamMemberStatus[];
  hasDirectReports: boolean;
  // User context (for About Me card)
  userMemberId: string | null;
  userRole: string | null;
  userManagerName: string | null;
  userDirectReports: DirectReport[];
};

export async function load(args: LoaderArgs): Promise<HomeState> {
  const requestedOrgId = args.params.orgId ?? null;

  const [authResult, orgsResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  // If profile not complete (no name), redirect to profile completion
  if (!authResult.user.name) {
    throw new RedirectError("/signup/complete");
  }

  const orgs = orgsResult.organizations;

  // Use requested org if valid, otherwise first org
  const initialOrgId = requestedOrgId
    ? (orgs.find((o) => o.id === requestedOrgId)?.id ?? orgs[0]?.id ?? null)
    : (orgs[0]?.id ?? null);

  // Load forms and history for the first org (if any)
  let initialForms: AssignedForm[] = [];
  let formAssignments: FormAssignment[] = [];
  let initialHistory: HistoryItem[] = [];
  let initialHasMore = false;
  let teamMembers: TeamMemberStatus[] = [];
  let hasDirectReports = false;
  let userRole: string | null = null;
  let userManagerName: string | null = null;
  let userDirectReports: DirectReport[] = [];
  let userMemberId: string | null = null;

  if (initialOrgId) {
    const [
      formsResult,
      assignmentsResult,
      historyResult,
      teamResult,
      memberContextResult,
    ] = await Promise.all([
      rpc.submissions.myForms({ orgId: initialOrgId }),
      rpc.submissions.myAssignments({ orgId: initialOrgId }),
      rpc.submissions.myHistory({
        orgId: initialOrgId,
        limit: HISTORY_PAGE_SIZE,
        offset: 0,
      }),
      // Fetch direct reports for team section (pass current week for period filtering)
      rpc.team.getMyTeam({
        workspaceId: initialOrgId,
        periodStart: getMondayFromWeekKey(
          getWeekKeyFromDate(new Date()),
        ).toISOString(),
      }),
      // Fetch current user's member context (role, manager)
      rpc.members.getMyContext({ workspaceId: initialOrgId }),
    ]);
    initialForms = formsResult.forms;
    formAssignments = assignmentsResult.assignments;
    initialHistory = historyResult.submissions;
    initialHasMore = historyResult.hasMore;
    hasDirectReports = teamResult.hasReports;
    teamMembers = teamResult.team.map((m) => ({
      id: m.id,
      name: m.name,
      email: m.email,
      hasSubmitted: m.latestSubmission !== null,
      submittedAt: m.latestSubmission?.submittedAt,
      submissionId: m.latestSubmission?.id,
      formId: m.latestSubmission?.formId,
    }));

    // User context for About Me card
    userMemberId = memberContextResult.memberId;
    userRole = memberContextResult.role;
    userManagerName = memberContextResult.managerName;
    userDirectReports = teamResult.team.map((m) => ({
      id: m.id,
      name: m.name,
    }));
  }

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    organizations: orgs,
    initialOrgId,
    initialForms,
    formAssignments,
    initialHistory,
    initialHasMore,
    requestedOrgId,
    teamMembers,
    hasDirectReports,
    userMemberId,
    userRole,
    userManagerName,
    userDirectReports,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Home");
  const {
    currentUser,
    mimic,
    organizations,
    initialOrgId,
    initialForms,
    formAssignments,
    initialHistory,
    initialHasMore,
    requestedOrgId,
    teamMembers,
    hasDirectReports,
    userMemberId,
    userRole,
    userManagerName,
    userDirectReports,
  } = state.value;

  const currentOrg = organizations.find((o) => o.id === initialOrgId);
  const isOrgAdmin = currentOrg ? isAdmin(currentOrg.role) : false;

  const { history, hasMore, loadingMore, loadMoreError, loadMore } = useHistory(
    {
      orgId: initialOrgId,
      initialHistory,
      initialHasMore,
    },
  );

  // Persist org selection to localStorage, or redirect to saved org
  useEffect(() => {
    if (typeof localStorage === "undefined") return;

    if (requestedOrgId) {
      // User navigated to specific org - save preference
      localStorage.setItem("pulse:orgId", requestedOrgId);
    } else {
      // User visited / without org param - check for saved preference
      const savedOrgId = localStorage.getItem("pulse:orgId");
      if (savedOrgId && organizations.some((o) => o.id === savedOrgId)) {
        navigateTo(`/orgs/${savedOrgId}`);
      }
    }
  }, [requestedOrgId, organizations]);

  // No orgs - show welcome/create screen
  if (!currentOrg) {
    return (
      <AppLayout user={currentUser} mimic={mimic}>
        <div class="flex flex-col items-center justify-center py-20">
          <div class="text-center max-w-md">
            <h1 class="text-[1.75rem] font-bold text-text tracking-[-0.01em] mb-3">
              Welcome to Pulse
            </h1>
            <p class="text-base text-text-muted leading-relaxed mb-8">
              Check in on people over time through scheduled forms.
            </p>
            <a
              href="/orgs/new"
              class="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold bg-text text-surface hover:bg-text-secondary transition-colors duration-150"
            >
              <IcoPlus class="size-4" />
              Create Organization
            </a>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Sidebar with user context (About Me card)
  const sidebar = (
    <UserContextCard
      userName={mimic?.memberName ?? currentUser.name ?? ""}
      userRole={userRole}
      userProfilePictureUrl={currentUser.profilePictureUrl}
      managerName={userManagerName}
      directReports={userDirectReports}
      orgId={currentOrg.id}
      memberId={userMemberId ?? undefined}
    />
  );

  // Show current org with branded layout
  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={currentOrg.id}
      orgName={currentOrg.name}
      brandColor={currentOrg.brandColor}
      activeTab="home"
      isAdmin={isOrgAdmin}
      organizations={organizations}
      formCount={currentOrg.formCount}
      hasReports={currentOrg.hasReports}
      sidebar={sidebar}
    >
      {/* Greeting */}
      <PersonalizedGreeting
        userName={mimic?.memberName ?? currentUser.name ?? ""}
      />

      {/* ============================================================ */}
      {/* MY CHECK-INS SECTION                                         */}
      {/* ============================================================ */}
      <section class="mt-8">
        <h2 class="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
          My Check-ins
        </h2>

        {/* Pending Check-in Card */}
        <PendingCheckInCard forms={initialForms} />

        {/* Status card when no pending forms but assigned to forms */}
        {initialForms.length === 0 && formAssignments.length > 0 && (
          <AssignmentStatusCard assignments={formAssignments} />
        )}

        {/* Past submissions */}
        {history.value.length > 0 && (
          <div
            class={
              initialForms.length > 0 || formAssignments.length > 0
                ? "mt-4"
                : ""
            }
          >
            <div class="rounded-xl border border-border bg-surface overflow-hidden">
              <div class="px-5 py-3 border-b border-border">
                <h3 class="text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Past submissions
                </h3>
              </div>
              <div class="px-4 py-3">
                <GroupedHistoryList items={history.value} />
              </div>
              {hasMore.value && (
                <div class="flex flex-col items-center gap-2 px-5 py-3.5 border-t border-border">
                  {loadMoreError.value && (
                    <p class="text-xs text-red-600 dark:text-red-400">
                      {loadMoreError.value}
                    </p>
                  )}
                  <Button
                    variant="secondary"
                    onClick={loadMore}
                    loading={loadingMore.value}
                    class="self-center"
                  >
                    {loadMoreError.value ? "Retry" : "Load more"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state when no pending forms, no assignments, and no history */}
        {initialForms.length === 0 &&
          formAssignments.length === 0 &&
          history.value.length === 0 && (
            <div class="rounded-xl border border-border bg-surface px-6 py-10 text-center">
              <div class="size-10 mx-auto mb-3 rounded-full bg-surface-muted flex items-center justify-center">
                <IcoFileText class="size-5 text-text-muted" />
              </div>
              <h3 class="text-sm font-semibold text-text mb-1">
                No check-ins yet
              </h3>
              <p class="text-sm text-text-muted max-w-xs mx-auto leading-relaxed">
                When you're assigned a check-in form, it will appear here.
              </p>
            </div>
          )}
      </section>

      {/* ============================================================ */}
      {/* TEAM CHECK-INS SECTION (only if user has direct reports)     */}
      {/* ============================================================ */}
      {hasDirectReports && (
        <TeamCheckInsCard members={teamMembers} orgId={currentOrg.id} />
      )}
    </BrandedLayout>
  );
}

// ============================================================================
// Assignment Status Card (shown when no active check-in but user has assignments)
// ============================================================================

function AssignmentStatusCard({
  assignments,
}: {
  assignments: FormAssignment[];
}) {
  const first = assignments[0];
  if (!first) return null;

  const assignmentText =
    assignments.length === 1
      ? first.title
      : `${assignments.length} check-in forms`;

  // Find the nearest future period_end across all assignments
  const nextDue = assignments
    .filter((a) => a.isRecurring && a.periodEnd)
    .map((a) => new Date(a.periodEnd!))
    .sort((a, b) => a.getTime() - b.getTime())[0];

  return (
    <div class="rounded-xl border border-border bg-surface p-5">
      <div class="flex items-start gap-3">
        <div class="size-9 rounded-full bg-surface-muted flex items-center justify-center flex-shrink-0 mt-0.5">
          <IcoCalendar class="size-4.5 text-text-muted" />
        </div>
        <div>
          <p class="text-sm font-medium text-text">No check-in due right now</p>
          <p class="text-sm text-text-muted mt-0.5">
            Assigned to {assignmentText}
            {nextDue && (
              <>
                <span class="mx-1 text-text-subtle">&middot;</span>
                Next due {formatDateShort(nextDue)}
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
